// src/components/common/ConsoleHeader.tsx
//
// 페이지 공용 헤더 — 목록/모니터/작성 페이지 상단에서 같은 리듬을 유지한다.
// 시각 언어의 정본은 HomePage.jsx · LoginPage.tsx · DashboardLayout.tsx.
//
//  · 제목 20~22px / 700 / 잉크(#171A1E)
//  · 서브텍스트 13px / #6B7178
//  · 우측 액션은 테마 기본 버튼(contained = 액센트, outlined = 흰 바탕)
//
// 구 "Cargo Manifest" 잔재(다크 슬랩, hazard tape, 도트 그리드, mono 대문자
// 코드 라벨, radius 0, 3px 오프셋 그림자)는 모두 제거되었다.
// props API 는 기존 사용처 호환을 위해 그대로 유지한다.

import { Box, Typography } from "@mui/material";
import { designTokens } from "../../theme.ts";

const SANS = designTokens.fonts.sans;

const C = {
  ink: "#171A1E",
  sub: "#6B7178",
  caption: "#9AA0A6",
  border: "#E4E7EA",
  surface: "#FFFFFF",
  field: "#F9FAFB",
};

export interface ConsoleHeaderProps {
  /** 페이지 식별 코드(예: "MARS · ESTIMATE · INDEX"). 더 이상 장식 라벨로
   *  렌더하지 않고 DOM data 속성으로만 남긴다. */
  code: string;
  /** 페이지 제목 */
  title: string;
  /** 제목 아래 서브텍스트 */
  meta?: React.ReactNode;
  /** 우측 정렬 액션 버튼 */
  actions?: React.ReactNode;
  /** @deprecated hazard tape 제거로 더 이상 사용하지 않음 (호환용) */
  hazardSide?: "left" | "right";
}

export default function ConsoleHeader({
  code,
  title,
  meta,
  actions,
}: ConsoleHeaderProps) {
  return (
    <Box
      data-page-code={code}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "flex-end" },
        justifyContent: "space-between",
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: { xs: 20, md: 22 },
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
            color: C.ink,
          }}
        >
          {title}
        </Typography>

        {meta && (
          <Box
            sx={{
              mt: 0.75,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
              fontFamily: SANS,
              fontSize: 13,
              lineHeight: 1.5,
              color: C.sub,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {meta}
          </Box>
        )}
      </Box>

      {actions && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexShrink: 0,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* 서브텍스트 헬퍼 — "2026.09.01 기준 · 견적서 12건"                    */
/* ------------------------------------------------------------------ */

// recordCountLabel 은 사용처가 넘긴 값을 그대로 쓴다 —
// 영문 대문자 코드를 한글로 옮기던 룩업 레이어는 제거되었다(사용처가 이미
// 최종 표시값을 넘긴다).
export function ConsoleHeaderMeta({
  recordCountLabel,
  count,
}: {
  recordCountLabel: string;
  count: number;
}) {
  const today = new Date();
  const stamp = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;
  const noun = (recordCountLabel ?? "").trim();

  return (
    <>
      <Box component="span">{stamp} 기준</Box>
      <Box component="span" aria-hidden sx={{ color: C.caption }}>
        ·
      </Box>
      <Box component="span">
        {noun ? `${noun} ` : ""}
        {Number(count ?? 0).toLocaleString("ko-KR")}건
      </Box>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 테이블 카드 — DataGrid 를 감싸는 흰 서피스                           */
/* ------------------------------------------------------------------ */

export function LedgerFrame({
  label = "원장",
  rows,
  children,
}: {
  label?: string;
  rows: number;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: C.field,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: C.ink }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: 12.5,
            color: C.sub,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {Number(rows ?? 0).toLocaleString("ko-KR")}건
        </Typography>
      </Box>

      <Box
        sx={{
          // 카드가 이미 보더/라운드를 갖고 있으므로 내부 그리드는 테두리 제거.
          // 나머지 표 스타일은 theme.ts 의 MuiDataGrid 오버라이드를 그대로 따른다.
          "& .MuiDataGrid-root": {
            border: "none",
            borderRadius: 0,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
